import { useEffect, useMemo, useRef, useState } from 'react';
import _ from 'lodash';
import { useMutation } from '@apollo/client';
import useIsInViewport from 'use-is-in-viewport';
import { v4 as uuid } from 'uuid';

import {
  getApplyLink,
  GetApplyLinkAnalytics,
  getCookie,
  getMessages,
  transformApplyLink,
} from 'src/utils/analytics/utils';
import { client } from 'src/utils/apollo-client';
import { PublishAnalyticsEventsDocument } from 'src/generated/graphql';
import {
  Cookie,
  EventType,
  FinishType,
  PageActionName,
  PageActionType,
  SegmentEventType,
} from './constants';
import { CardsMarketplaceOffer } from 'src/types/marketplace';
import {
  useAnalyticsSkuViewedContext,
  useAnalytics,
  useAnalyticsPageView,
} from '@nerdwallet-cards/analytics';
import { EventPayload } from './Events';
import { createRedirectLink } from './segment/utils';
import { useAppSelector } from 'src/redux/hooks';
import { getAuth0AnonymousId, getAuth0State } from 'src/redux/selectors';

export function useUserNavigatedEvent() {
  const navigationId = useMemo(uuid, []);
  const hasFired = useRef(false);
  const [mutate, { error }] = useMutation(PublishAnalyticsEventsDocument, {
    client,
  });

  if (error) {
    console.error(error);
  }

  useEffect(() => {
    if (!hasFired.current) {
      hasFired.current = true;
      mutate({
        variables: {
          messages: getMessages(EventType.UserNavigated, {
            navigation_id: navigationId,
            url: window.location.href,
            initially_visible: !window.document.hidden,
            web_referrer: window.document.referrer || undefined,
            viewport_width: window.innerWidth,
            viewport_height: window.innerHeight,
            pixel_ratio: window.devicePixelRatio,
            google_analytics_client_id: getCookie(Cookie.GoogleAnalytics),
          }),
        },
      });
    }
  }, [mutate, navigationId]);

  return { navigationId };
}

type UseImpressionEventProps<TType extends EventType> = {
  eventType: TType;
  immediate?: boolean;
  navigationId: string;
  payload: EventPayload<TType>;
};

export function useImpressionEvent<TType extends EventType>(
  {
    eventType,
    immediate = false,
    navigationId,
    payload,
  }: UseImpressionEventProps<TType>,
  filterOut?: boolean
) {
  const [isInViewport, ref] = useIsInViewport();
  const impressionId = useRef<string>(uuid());
  const hasFired = useRef<typeof payload | null>(null);

  const [mutate, { error }] = useMutation(PublishAnalyticsEventsDocument, {
    client,
  });

  if (error) {
    console.error(error);
  }

  useEffect(() => {
    if (!(isInViewport || immediate)) {
      return;
    }
    if (filterOut) {
      return;
    }
    if (_.isEqual(payload, hasFired.current)) {
      return;
    }
    if (hasFired.current) {
      impressionId.current = uuid();
    }

    mutate({
      variables: {
        messages: getMessages(eventType, {
          ...payload,
          impression_id: impressionId.current,
          navigation_id: navigationId,
        }),
      },
    });
    hasFired.current = payload;
  }, [
    eventType,
    filterOut,
    immediate,
    isInViewport,
    mutate,
    navigationId,
    payload,
  ]);

  return { impressionId: impressionId.current, ref };
}

type UseCallbackEvent = {
  impressionId?: string;
  navigationId?: string;
};
export function useCallbackEvent({
  impressionId,
  navigationId,
}: UseCallbackEvent) {
  const [mutate, { error }] = useMutation(PublishAnalyticsEventsDocument, {
    client,
  });

  if (error) {
    console.error(error);
  }

  return <TEventType extends EventType>(
    eventType: TEventType,
    payload: EventPayload<TEventType>
  ) =>
    mutate({
      variables: {
        messages: getMessages(eventType, {
          ...payload,
          impression_id: impressionId,
          navigation_id: navigationId,
        }),
      },
    });
}

/**
 * React hook used to apply additional query parameters to the apply link. This abstraction is
 * necessary because we need the full, current URL and since `getApplyLink`
 * just pulls this from window.location.href and in order to prevent hydration errors, we need
 * to wrap this logic in a useEffect.
 */
export function useGetApplyLink(
  offer: CardsMarketplaceOffer,
  analytics: GetApplyLinkAnalytics
) {
  const [applyLinkParams, setApplyLinkParams] =
    useState<ReturnType<typeof getApplyLink>>(null);

  useEffect(() => {
    setApplyLinkParams(getApplyLink(offer, analytics));
  }, [offer, analytics]);

  return applyLinkParams;
}

type UseInitializeGtm = {
  isSignedIn: boolean;
  navigationId: string;
};
export function useInitializeGtm({
  isSignedIn,
  navigationId,
}: UseInitializeGtm) {
  const hasFired = useRef<boolean>(false);

  useEffect(() => {
    if (hasFired.current) {
      return;
    }

    hasFired.current = true;
    window.dataLayer = window.dataLayer ?? [];
    window.dataLayer.push(
      {
        gtmPageVertical: 'Credit Cards',
        gtmQuantcastLabel: 'Vertical.Credit Cards',
        gtmPageTopic: 'Recommended Credit Cards',
        gtmPageType: 'Transact',
        gtmContentType: 'Compare',
        gtmContentClusters: '',
        isNWMobileApp: 'false',
        isLoggedIn: `${isSignedIn}`,
        gtmAttrVertical: '',
        gtmAttrTopic: '',
        gtmPrimaryCluster: '',
        // needed for DataLayerEvent type
        event: '',
      },
      {
        'gtm.start': new Date().getTime(),
        event: 'gtm.js',
      },
      {
        gtmPageViewId: navigationId,
        gtmCookieId: getCookie('cookie_id')?.substring(40),
        event: 'nwa_values_ready',
      }
    );
  }, [isSignedIn, navigationId]);
}

export const useSegmentAwareFinishLink = (
  offer: CardsMarketplaceOffer,
  analytics: GetApplyLinkAnalytics
) => {
  const anonymousId = useAnonymousId();
  const applyLink = useGetApplyLink(offer, {
    ...analytics,
  });
  const { fireShoppingFinishedEvent, skuViewedPayload, shoppingFinishedId } =
    useAnalyticsSkuViewedContext();

  const finishApplyLink = useMemo(() => {
    if (applyLink) {
      return transformApplyLink({ applyLink, shoppingFinishedId });
    }
  }, [applyLink, shoppingFinishedId]);

  const shoppingFinishedPayload = {
    shopping_finish_id: shoppingFinishedId,
    anonymous_id: anonymousId,
    interstitial_client_bot_suspected: false,
    cta_type: analytics.linkType,
    finish_type: FinishType.ExternalApplication,
    ...skuViewedPayload,
  };

  const redirectLink = finishApplyLink
    ? createRedirectLink({
        applyLink: finishApplyLink,
        params: shoppingFinishedPayload,
      })
    : applyLink;

  return {
    applyLink: finishApplyLink,
    redirectLink,
    fireShoppingFinishedEvent,
  };
};

export const useAnonymousId = (): string | null => {
  const { analytics } = useAnalytics();
  const anonymousId = useRef<string | null>(null);
  const auth0AnonymousId = useAppSelector(getAuth0AnonymousId) ?? null;
  const auth0State = useAppSelector(getAuth0State);
  useEffect(() => {
    const getUserData = async () => {
      const user = await analytics?.segmentInstance?.user();
      //if it's not set it will generate a new one
      const userAnonymousId = user?.anonymousId();
      if (userAnonymousId) anonymousId.current = userAnonymousId;
    };
    // this is to avoid needing to call this function again if we already have an anonymous id
    !auth0State && getUserData();
  }, [analytics]);
  return auth0State ? auth0AnonymousId : anonymousId.current;
};

type TrackPageActionPayload = {
  actionName: PageActionName;
  actionType: PageActionType;
  actionInput: string | null;
  activityId?: string;
  additionalAttributes?: Record<string, any>;
  actionSection?: string;
};

export const useSegmentPageAction = () => {
  const { analytics, deployableName, deployableVersion, deviceId } =
    useAnalytics();
  const { pageName, pageViewId } = useAnalyticsPageView();

  return ({
    actionName,
    actionType,
    actionInput,
    activityId,
    additionalAttributes,
  }: TrackPageActionPayload) => {
    analytics?.track(SegmentEventType.PageAction, {
      action_input: actionInput,
      action_name: actionName,
      action_type: actionType,
      activity_id: activityId,
      additional_attributes: additionalAttributes,
      deployable_name: deployableName,
      deployable_version: deployableVersion,
      impression_page_name: pageName,
      impression_page_url: window.location.href,
      impression_pageview_id: pageViewId,
      traditional_device_id: deviceId,
    });
  };
};
